import { DealTypeEnumType } from '__generated__/graphql';
import { Navigate } from 'react-router-dom';
import Documents from 'routes/Documents';
import DocumentsAndVaultAccounts from 'routes/Documents/DocumentsAndVaultAccounts';
import Help from 'routes/Help';

import MessagingPanel from 'components/Conversation/MessagingPanel';
import ProtectedRoute from 'components/ProtectedRoute';

import AccreditationPage from './Accreditation';
import AccreditationOnlinePage from './AccreditationOnline';
import Dashboard from './Dashboard';
import DealPage from './Deal';
import DealInvitePage from './DealInvite';
import DealInviteConfirmPage from './DealInviteConfirm';
import DealInvites from './DealInvites';
import ActiveInvitesOfUser from './DealInvites/ActiveInvites';
import CommitmentsOfUser from './DealInvites/Commitments';
import PassedInvitesOfUser from './DealInvites/PassedInvites';
import ExploreDeals from './ExploreDeals';
import FundPage from './Fund';
import FundInvitesPage from './FundInvites';
import FundDealConsentInvite from './FundInvites/FundDealConsentInvite';
import FundInviteConfirm from './FundInvites/FundInviteConfirm';
import FundInviteLPConsent from './FundInvites/FundInviteLPConsent';
import InvestPageLayout from './InvestPage/InvestPageLayout';
import { INVEST_HUB_DEAL_INVITES_PATH, INVEST_HUB_PATH } from './InvestPage/investHubPaths';
import InvestmentEntities from './InvestmentEntities';
import CreateInvestmentEntity from './InvestmentEntities/CreateInvestmentEntity';
import BasicInformationInvestmentEntity from './InvestmentEntities/CreateInvestmentEntity/BasicInformation';
import InvestorApplication from './InvestmentEntities/CreateInvestmentEntity/InvestorApplication';
import Kyc from './InvestmentEntities/CreateInvestmentEntity/Kyc';
import SubmitInvestmentEntityForReview from './InvestmentEntities/CreateInvestmentEntity/SubmitForReview';
import InvestmentEntity from './InvestmentEntity';
import InvestmentEntityBankAccount from './InvestmentEntity/BankAccount';
import InvestmentEntityDematAccount from './InvestmentEntity/DematAccount';
import InvestmentEntityDocuments from './InvestmentEntity/Documents';
import InvestmentEntityInformation from './InvestmentEntity/Information';
import InvestmentEntityInvestments from './InvestmentEntity/Investments';
import InvestmentEntityKyc from './InvestmentEntity/Kyc';
import UnitStatement from './InvestmentEntity/UnitStatement';
import InvestorConversations from './InvestorConversations';
import OisterDealPage from './OisterDeal';
import Posts from './Posts';
import FundPosts from './Posts/FundPosts';
import SyndicatePosts from './Posts/SyndicatePosts';
import Settings from './Settings';
import GeneralSettings from './Settings/General';
import InvestmentPreferencesSettingsPage from './Settings/InvestmentPreferences';
import NotificationSettingsPage from './Settings/Notifications';
import SecuritySettingsPage from './Settings/Security';
import Syndicates from './Syndicates';
import InvestorApplications from './Syndicates/Applications';
import IncomingSyndicateInvites from './Syndicates/Applications/Incoming';
import OutgoingSyndicateApplications from './Syndicates/Applications/Outgoing';
import BackedSyndicates from './Syndicates/Backed';
import SuggestedSyndicates from './Syndicates/Suggested';
import { UnitTransferListContent } from './UnitTransfer';
import WishlistPage from './Wishlist';
import Wrapped2025Page from './Wrapped2025';

const routes = [
  {
    path: '/invest',
    children: [
      {
        element: (
          <ProtectedRoute>
            <InvestPageLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <ExploreDeals dealType={DealTypeEnumType.Syndicated} />,
          },
          {
            path: 'ruvs',
            element: <ExploreDeals dealType={DealTypeEnumType.Ruv} />,
          },
          {
            path: 'deal-invites',
            element: <DealInvites />,
            children: [
              {
                index: true,
                element: <ActiveInvitesOfUser />,
              },
              {
                path: 'committed',
                element: <CommitmentsOfUser />,
              },
              {
                path: 'passed',
                element: <PassedInvitesOfUser />,
              },
            ],
          },
        ],
      },
      {
        path: 'portfolio',
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      // Backward compat: old /invest/invest/... paths
      {
        path: 'invest',
        element: <Navigate to={INVEST_HUB_PATH} replace />,
      },
      // Baskets is retired: every legacy basket/encase path lands on the Invest hub
      {
        path: 'invest/baskets/*',
        element: <Navigate to={INVEST_HUB_PATH} replace />,
      },
      {
        path: 'baskets/*',
        element: <Navigate to={INVEST_HUB_PATH} replace />,
      },
      {
        path: 'invest/deal-invites',
        element: <Navigate to={INVEST_HUB_DEAL_INVITES_PATH} replace />,
      },
      {
        path: 'invest/deal-invites/committed',
        element: <Navigate to={`${INVEST_HUB_DEAL_INVITES_PATH}/committed`} replace />,
      },
      {
        path: 'invest/deal-invites/passed',
        element: <Navigate to={`${INVEST_HUB_DEAL_INVITES_PATH}/passed`} replace />,
      },
      {
        path: 'explore-deals',
        element: <Navigate to={INVEST_HUB_PATH} replace />,
      },
      {
        path: 'invest/encase/*',
        element: <Navigate to={INVEST_HUB_PATH} replace />,
      },
      {
        path: 'deal/:dealId',
        element: (
          <ProtectedRoute>
            <DealPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'fund/:fundId',
        element: (
          <ProtectedRoute>
            <FundPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'fund-invites/:fundInviteLinkId',
        element: (
          <ProtectedRoute>
            <FundInvitesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'fund-invites/:fundInviteLinkId/confirm',
        element: (
          <ProtectedRoute>
            <FundInviteConfirm />
          </ProtectedRoute>
        ),
      },
      {
        path: 'fund-deal-consent/:fundDealInviteId',
        element: (
          <ProtectedRoute>
            <FundDealConsentInvite />
          </ProtectedRoute>
        ),
      },
      {
        path: 'fund-invest-consent/:fundId/deal/:dealId/closing/:dealClosingId',
        element: (
          <ProtectedRoute>
            <FundInviteLPConsent />
          </ProtectedRoute>
        ),
      },
      {
        path: 'deal/oister/:userId',
        element: (
          <ProtectedRoute>
            <OisterDealPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'deal-invites/:dealInviteId/confirm',
        element: (
          <ProtectedRoute>
            <DealInviteConfirmPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'deal-invites/:dealInviteId',
        element: (
          <ProtectedRoute>
            <DealInvitePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'investment-entities',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <InvestmentEntities />
              </ProtectedRoute>
            ),
          },
          {
            path: ':investmentEntityId',
            element: (
              <ProtectedRoute>
                <InvestmentEntity />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute>
                    <InvestmentEntityInformation />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'kyc',
                element: (
                  <ProtectedRoute>
                    <InvestmentEntityKyc />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'demat',
                element: (
                  <ProtectedRoute>
                    <InvestmentEntityDematAccount />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'bankAccounts',
                element: (
                  <ProtectedRoute>
                    <InvestmentEntityBankAccount />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'documents',
                element: (
                  <ProtectedRoute>
                    <InvestmentEntityDocuments />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'investments',
                element: (
                  <ProtectedRoute>
                    <InvestmentEntityInvestments />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'unit-transfers',
                element: (
                  <ProtectedRoute>
                    <UnitTransferListContent />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute>
                <CreateInvestmentEntity />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute>
                    <BasicInformationInvestmentEntity />
                  </ProtectedRoute>
                ),
              },
              {
                path: ':investmentEntityId/basic-information',
                element: (
                  <ProtectedRoute>
                    <BasicInformationInvestmentEntity />
                  </ProtectedRoute>
                ),
              },
              {
                path: ':investmentEntityId/kyc',
                element: (
                  <ProtectedRoute>
                    <Kyc />
                  </ProtectedRoute>
                ),
              },
              {
                path: ':investmentEntityId/investor-application',
                element: (
                  <ProtectedRoute>
                    <InvestorApplication />
                  </ProtectedRoute>
                ),
              },
              {
                path: ':investmentEntityId/submit-for-review',
                element: (
                  <ProtectedRoute>
                    <SubmitInvestmentEntityForReview />
                  </ProtectedRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'investment-entities/:investmentEntityId/unit-statement',
        element: (
          <ProtectedRoute>
            <UnitStatement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'syndicates',
        element: (
          <ProtectedRoute>
            <Syndicates />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <SuggestedSyndicates />
              </ProtectedRoute>
            ),
          },
          {
            path: ':syndicateId',
            element: (
              <ProtectedRoute>
                <SuggestedSyndicates />
              </ProtectedRoute>
            ),
          },
          {
            path: 'backed',
            element: (
              <ProtectedRoute>
                <BackedSyndicates />
              </ProtectedRoute>
            ),
          },
          {
            path: 'backed/:syndicateId',
            element: (
              <ProtectedRoute>
                <BackedSyndicates />
              </ProtectedRoute>
            ),
          },
          {
            path: 'pending',
            element: (
              <ProtectedRoute>
                <InvestorApplications />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute>
                    <OutgoingSyndicateApplications />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'incoming',
                element: (
                  <ProtectedRoute>
                    <IncomingSyndicateInvites />
                  </ProtectedRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <GeneralSettings />
              </ProtectedRoute>
            ),
          },
          {
            path: 'notifications',
            element: (
              <ProtectedRoute>
                <NotificationSettingsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'security',
            element: (
              <ProtectedRoute>
                <SecuritySettingsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'investment-preferences',
            element: (
              <ProtectedRoute>
                <InvestmentPreferencesSettingsPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'conversations',
        element: (
          <ProtectedRoute>
            <InvestorConversations />
          </ProtectedRoute>
        ),
        children: [
          {
            path: ':conversationId',
            element: (
              <ProtectedRoute>
                <MessagingPanel />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'posts',
        element: (
          <ProtectedRoute>
            <Posts />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <SyndicatePosts />
              </ProtectedRoute>
            ),
          },
          {
            path: 'fund-posts',
            element: (
              <ProtectedRoute>
                <FundPosts />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'documents',
        element: (
          <ProtectedRoute>
            <Documents />
          </ProtectedRoute>
        ),
      },
      {
        path: 'documents/:investmentEntityId',
        element: (
          <ProtectedRoute>
            <DocumentsAndVaultAccounts />
          </ProtectedRoute>
        ),
      },
      {
        path: 'wishlist',
        element: <WishlistPage />,
      },
      {
        path: 'accreditation',
        element: (
          <ProtectedRoute>
            <AccreditationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'apply-for-accreditation',
        element: (
          <ProtectedRoute>
            <AccreditationOnlinePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'wrapped-2025',
        element: (
          <ProtectedRoute>
            <Wrapped2025Page />
          </ProtectedRoute>
        ),
      },
      {
        path: 'help',
        element: <Help />,
      },
    ],
  },
];

export default routes;
